import Icon from '@/components/ui/AppIcon';

interface Deliverable {
  id: string;
  title: string;
  description: string;
  icon: string;
  features: string[];
}

export default function DeliverablesGrid() {
  const deliverables: Deliverable[] = [
    {
      id: 'reels',
      title: 'Reels & Videos',
      description: 'Engaging short-form video content optimized for maximum reach and engagement.',
      icon: 'VideoCameraIcon',
      features: ['Trending audio integration', 'Professional editing', 'Caption optimization', 'Hashtag strategy'],
    },
    {
      id: 'posters',
      title: 'Posters & Graphics',
      description: 'Eye-catching visual content designed to stop the scroll and drive action.',
      icon: 'PhotoIcon',
      features: ['Custom designs', 'Brand consistency', 'Multiple formats', 'A/B testing'],
    },
    {
      id: 'profile',
      title: 'Profile Management',
      description: 'Complete handling of your social media presence across all platforms.',
      icon: 'UserCircleIcon',
      features: ['Daily posting', 'Community engagement', 'DM management', 'Profile optimization'],
    },
    {
      id: 'ads',
      title: 'Paid Advertising',
      description: 'Strategic paid campaigns that deliver measurable ROI and business growth.',
      icon: 'MegaphoneIcon',
      features: ['Audience targeting', 'Budget optimization', 'Ad creative testing', 'Performance tracking'],
    },
    {
      id: 'analytics',
      title: 'Analytics & Reporting',
      description: 'Comprehensive insights and data-driven recommendations for continuous improvement.',
      icon: 'PresentationChartLineIcon',
      features: ['Monthly reports', 'Growth metrics', 'Competitor analysis', 'Strategy refinement'],
    },
    {
      id: 'strategy',
      title: 'Content Strategy',
      description: 'Strategic planning and execution aligned with your business objectives.',
      icon: 'LightBulbIcon',
      features: ['Content pillars', 'Posting schedule', 'Campaign planning', 'Trend integration'],
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-[2px] h-4 bg-gradient-to-b from-pink-500 to-purple-600" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
              What You Get
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Complete Social Media
            <br />
            <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              Deliverables
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Everything you need to dominate social media and grow your business online.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {deliverables.map((item, idx) => (
            <div
              key={item.id}
              className="reveal"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-pink-500/30 transition-all glow-hover h-full group">
                <div className="w-14 h-14 mb-5 flex items-center justify-center text-pink-500 bg-gradient-to-br from-pink-500/10 to-purple-600/10 rounded-xl group-hover:scale-110 transition-transform">
                  <Icon name={item.icon as any} size={28} />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{item.title}</h3>
                <p className="text-zinc-400 mb-6 leading-relaxed">
                  {item.description}
                </p>
                <ul className="space-y-2">
                  {item.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-start gap-2 text-sm text-zinc-400">
                      <Icon name="CheckCircleIcon" size={16} className="text-pink-500 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center reveal">
          <div className="bg-gradient-to-r from-pink-500/10 to-purple-600/10 border border-pink-500/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              And So Much More!
            </h3>
            <p className="text-zinc-300 text-lg leading-relaxed">
              Our social media plans include everything from initial strategy development to ongoing optimization. We handle stories, carousel posts, influencer collaborations, contest management, crisis response, and comprehensive social listening. Your success is our mission.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}